import { useMemo } from 'react'
import { useGetProjectGridFields } from '@/service-library/hooks/project-grid-fields'
import { PositionLookup } from '@/types/position-lookups'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'

export type PositionStartingPoint = {
  infoTypeId: string
  label: string
  /** The project's fields carrying this info type, e.g. `Line Items • Job`. */
  fieldNames: string[]
}

/**
 * A position saves the kind of value it starts from, not which field holds it.
 * So two fields of the same kind — a Job in the header and a Job on the line
 * items — are one starting point here, naming both fields underneath.
 */
export function useProjectStartingPoints() {
  const { project } = useProjectContext()

  const { projectGridFields, isPending, isError } = useGetProjectGridFields({
    filters: {
      limit: '1000',
      project_id: project.id,
      fields__include: 'info_type,full_name',
      info_type__fields__only: 'id,name',
    },
  })

  const startingPointsByInfoTypeId = useMemo(() => {
    const byInfoType = new Map<string, PositionStartingPoint>()

    projectGridFields.forEach((field) => {
      const infoTypeId = field.info_type_id
      if (!infoTypeId) return

      const fieldName = field.full_name || field.name
      const existing = byInfoType.get(infoTypeId)
      if (existing) {
        if (!existing.fieldNames.includes(fieldName)) {
          existing.fieldNames.push(fieldName)
        }
        return
      }

      byInfoType.set(infoTypeId, {
        infoTypeId,
        label: field.info_type?.name || fieldName,
        fieldNames: [fieldName],
      })
    })

    return byInfoType
  }, [projectGridFields])

  return { startingPointsByInfoTypeId, isPending, isError }
}

/** Only the kinds this lookup accepts, and only where a field carries one. */
export default function usePositionStartingPoints(
  lookup: PositionLookup | undefined,
) {
  const { startingPointsByInfoTypeId, isPending, isError } =
    useProjectStartingPoints()
  const isScoped = !!lookup?.scope.required

  const startingPoints = useMemo(() => {
    if (!lookup?.scope.required) return []

    return lookup.scope.accepts
      .map((infoTypeId) => startingPointsByInfoTypeId.get(infoTypeId))
      .filter((startingPoint): startingPoint is PositionStartingPoint =>
        Boolean(startingPoint),
      )
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [lookup, startingPointsByInfoTypeId])

  return {
    startingPoints,
    isPending: isScoped && isPending,
    // Kept apart from an empty list. "No field holds this" tells someone to
    // go add a field, and a failed request must not send them to do that.
    isError,
  }
}
